// @ts-ignore:next-line
import type { Module } from 'vuex'
import type { TagState } from './types'
import type { EditTag } from './actions'
import { tagActions } from './actions'
import { clone, mergeObjects } from '@/utils/helpers/'
import type { RootState } from '@/store/modules/types'
import type { Tag } from '@/types/resources/tag'

export const initialTagState = (): TagState => ({
  tags: [],
  tagsCount: 0,
  isFetchingTags: false,
  isNoMoreTags: false,
  hasFetchTagsFailed: false,
  isPatchingTag: false,
  isDeletingTag: false,
  isCreatingTag: false,
  isMergingTags: false,
  isGroupingTags: false,
})

const tagModule: Module<TagState, RootState> = {
  namespaced: true,
  state: initialTagState,
  actions: tagActions,
  getters: {
    bubbleUpFavoriteTags() {
      return (tags: Tag[]) => clone(tags).sort(
        (a: Tag, b: Tag) => {
          if (a.favourite && !b.favourite) return -1
          else if (!a.favourite && b.favourite) return 1
          else return 0
        },
      )
    },
    tagsList(state: TagState) {
      return state.tags
    },
  },
  mutations: {
    // Tag Pagination
    RESET_TAGS(state: TagState) {
      state.tags = []
      state.tagsCount = 0
      state.isNoMoreTags = false
      state.hasFetchTagsFailed = false
    },
    SET_TAGS(state: TagState, data) {
      state.tags = clone(data?.rows || [])
    },
    CONCAT_TAGS(state: TagState, data) {
      const newRows = data?.rows || []
      state.tags = clone(state.tags.concat(newRows))
    },
    SET_TAGS_COUNT(state: TagState, data) {
      state.tagsCount = data?.count || 0
    },
    SET_IS_NO_MORE_TAGS(state: TagState, data) {
      const rows = data?.rows || []
      const count = data?.count || 0
      state.isNoMoreTags = rows.length === 0 || count === state.tags.length + rows.length
    },
    SET_HAS_FETCH_TAGS_FAILED(state: TagState, hasFailed: boolean) {
      state.hasFetchTagsFailed = hasFailed
    },
    SET_IS_FETCHING_TAGS(state: TagState, isFetching: boolean) {
      state.isFetchingTags = isFetching
    },
    // Create Tag
    SET_IS_CREATING_TAG(state: TagState, isCreating: boolean) {
      state.isCreatingTag = isCreating
    },
    // Patch Tag
    SET_IS_PATCHING_TAG(state: TagState, isPatching: boolean) {
      state.isPatchingTag = isPatching
    },
    PATCH_TAG_IN_TAGS(state: TagState, { oid, editTag }: { oid: number, editTag: EditTag }) {
      let newTags: Tag[] = clone(state.tags)
      newTags = newTags.map((tag) => {
        if (oid === tag.oid) {
          return mergeObjects(tag, editTag)
        }
        else {
          return tag
        }
      })
      state.tags = newTags
    },
    // Delete Tag
    SET_IS_DELETING_TAG(state: TagState, isDeleting: boolean) {
      state.isDeletingTag = isDeleting
    },
    REMOVE_TAG_FROM_TAGS(state: TagState, oid: number) {
      const newTags: Tag[] = clone(state.tags)
      state.tags = newTags.filter(tag => tag.oid !== oid)
    },
    REMOVE_TAGS_FROM_TAGS(state: TagState, oids: number[]) {
      const oidsSet = new Set(oids)
      const newTags: Tag[] = clone(state.tags)
      state.tags = newTags.filter(tag => !oidsSet.has(tag.oid))
    },
    SET_IS_MERGING_TAGS(state: TagState, isMerging: boolean) {
      state.isMergingTags = isMerging
    },
    SET_IS_GROUPING_TAGS(state: TagState, isMerging: boolean) {
      state.isGroupingTags = isMerging
    },
  },
}

export default tagModule
